import type {
	AxiosError,
	AxiosHeaders,
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	CreateAxiosDefaults,
	InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import type { PlatformsEnum } from '../types/eunms';
import { AUTHENTICATION_FAILED, USERNAME_HEADER, ID_TOKEN_HEADER, ACCESS_TOKEN_HEADER } from './consts';
import { NetworkClient } from './network-client';
import type { RequestInterceptor, ResponseInterceptor, Tokens } from './types';
import { HttpStatusEnum } from './types';
import { getDebugHeaders, getTokens, logout, mapAxiosErrorToErrorResponse, refreshTokens } from './utils';

type AuthUrls = {
	refresh: string;
	logout: string;
	logoutRedirectUrl?: string;
};

const AUTH_STATUS_HEADER = 'x-auth-status';

export class NetworkClientBuilder {
	private readonly client: AxiosInstance;
	private static _includeCredentials: boolean = false;

	private constructor(
		config?: CreateAxiosDefaults,
		private includeCredentials = true
	) {
		this.client = axios.create(config);
	}

	/**
	 * Sets includeCredentials flag
	 * @param use {boolean} - set the flag
	 */
	static useCredentials(use: boolean): void {
		NetworkClientBuilder._includeCredentials = Boolean(use);
	}

	static get includeCredentials(): boolean {
		return NetworkClientBuilder._includeCredentials;
	}

	/**
	 * Creates a new instance of NetworkBuilder.
	 * @param config { CreateAxiosDefaults }
	 * @returns { NetworkClientBuilder } - A new instance of NetworkBuilder.
	 */
	public static create(config?: CreateAxiosDefaults): NetworkClientBuilder {
		return new NetworkClientBuilder(config);
	}

	/**
	 * Creates a new instance of NetworkBuilder with authentication handling.
	 * @param urls { refresh: string }
	 * @param onAuthError { () => Promise<void> | void }
	 * @param platform { PlatformsEnum } - The platform for which the authentication is being handled.
	 * @param config { CreateAxiosDefaults }
	 * @returns { NetworkClientBuilder } - A new instance of NetworkBuilder with authentication handling.
	 */
	public static createWithAuth(
		urls: { refresh: string; logout: string },
		onAuthError: (data?: any) => Promise<void> | void,
		platform: PlatformsEnum,
		config?: CreateAxiosDefaults
	): NetworkClientBuilder {
		return new NetworkClientBuilder(config).handleAuthentication(urls, onAuthError, platform);
	}

	/**
	 * Adds a request interceptor to the Axios instance.
	 * @param config { RequestInterceptor } - The request interceptor configuration.
	 * @returns { this } - The current instance of NetworkBuilder.
	 */
	public addRequestInterceptor(config: RequestInterceptor): this {
		this.client.interceptors.request.use(config.onRequest, config.onError);
		return this;
	}

	/**
	 * Adds a response interceptor to the Axios instance.
	 * @param config { ResponseInterceptor } - The response interceptor configuration.
	 * @returns { this } - The current instance of NetworkBuilder.
	 */
	public addResponseInterceptor(config: ResponseInterceptor): this {
		this.client.interceptors.response.use(config.onResponse, config.onError);
		return this;
	}

	/**
	 * Handles authentication errors by refreshing the token.
	 * @param urls { refresh: string } - The URLs for refreshing the token.
	 * @param onError { () => Promise<void> | void } - The function to call on authentication error.
	 * @param platform { PlatformsEnum } - The platform for which the authentication is being handled.
	 * @returns { this } - The current instance of NetworkBuilder.
	 */
	private handleAuthentication(urls: AuthUrls, onError: () => Promise<void> | void, platform: PlatformsEnum): this {
		this.client.interceptors.request.use(request => {
			const headers: Partial<Tokens> = getTokens();

			if (!request.headers.get(ID_TOKEN_HEADER) && !request.headers[ID_TOKEN_HEADER.toLowerCase()]) {
				request.headers.set(ID_TOKEN_HEADER, headers?.userIdToken);
			}

			if (!request.headers.get(ACCESS_TOKEN_HEADER) && !request.headers[ACCESS_TOKEN_HEADER.toLowerCase()]) {
				request.headers.set(ACCESS_TOKEN_HEADER, headers?.accessToken);
			}

			if (!request.headers.get(USERNAME_HEADER) && !request.headers[USERNAME_HEADER.toLowerCase()]) {
				request.headers.set(USERNAME_HEADER, headers?.username);
			}

			return request;
		});

		// retaining only one refresh token request at a time
		let refreshTokenPromise: Promise<AxiosResponse<Tokens>> | null = null;

		this.client.interceptors.response.use(
			response => response,
			(error: AxiosError): Promise<AxiosResponse> => {
				const response: AxiosResponse | undefined = error?.response;

				// Returning the error if it's a client error
				if (!error || axios.isCancel(error) || !response) {
					console.error('Client error', error);

					return Promise.resolve({ data: { err: error?.message?.toString() || null } } as AxiosResponse);
				}

				const authStatus: string =
					(response.headers as AxiosHeaders)?.get(AUTH_STATUS_HEADER) || response.headers[AUTH_STATUS_HEADER] || '';

				if (response.status === HttpStatusEnum.UNAUTHORIZED && authStatus === AUTHENTICATION_FAILED) {
					const originalConfig: InternalAxiosRequestConfig = response.config;

					const debugHeaders: Record<string, string> = getDebugHeaders(originalConfig);

					// if there is no refresh token promise, create one and store it in the variable
					if (!refreshTokenPromise) {
						refreshTokenPromise = refreshTokens(urls.refresh, platform, debugHeaders)
							.catch((error: AxiosError) => {
								refreshTokenPromise = null;

								console.error(`Unable to refresh token, error: '${error?.message}', logging out`);

								const redirectUrl: string = urls.logoutRedirectUrl ?? window.location.href;

								return logout(urls.logout, redirectUrl, onError, error?.message).then(() =>
									mapAxiosErrorToErrorResponse(error)
								);
							})
							.finally(() => (refreshTokenPromise = null));
					}

					// if there is a refresh token promise, wait for it to resolve and retry the request with the new tokens
					return refreshTokenPromise.then((response: AxiosResponse<Tokens>) => {
						const { accessToken, userIdToken } = response.data;

						const config: AxiosRequestConfig = {
							...originalConfig,
							withCredentials: NetworkClientBuilder.includeCredentials,
							headers: {
								...originalConfig.headers,
								...(accessToken && { [ACCESS_TOKEN_HEADER]: accessToken }),
								...(userIdToken && { [ID_TOKEN_HEADER]: userIdToken }),
							},
						};

						return axios(config).catch(error => mapAxiosErrorToErrorResponse(error));
					});
				}

				return Promise.resolve(mapAxiosErrorToErrorResponse(error));
			}
		);

		return this;
	}

	/**
	 * Builds the network handler with the configured Axios instance.
	 * @returns { NetworkClient } - The network handler with methods for making HTTP requests.
	 */
	public build(): NetworkClient {
		return new NetworkClient(this.client, NetworkClientBuilder.includeCredentials);
	}
}
